/**
 * webRtcManager.ts
 * Manages local WebRTC Peer-to-Peer DataChannel connections between
 * the referee's device (phone) and spectator display (tablet),
 * combined with automatic WebSocket reconnect, heartbeat, and diagnostics.
 */

export interface ScoreboardPayload {
  type: 'SCORE_UPDATE' | 'MATCH_RESET';
  player1: {
    name: string;
    score: number;
    gamesWon: number;
    isServing: boolean;
    pastGames: number[];
  };
  player2: {
    name: string;
    score: number;
    gamesWon: number;
    isServing: boolean;
    pastGames: number[];
  };
  server: 1 | 2;
  serviceBox: 'left' | 'right';
  gameNumber: number;
  matchStarted: boolean;
  timestamp: number;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SignalingMessage {
  type: 'join' | 'offer' | 'answer' | 'candidate' | 'joined' | 'peer-joined' | 'peer-left' | 'request-score' | 'ping';
  sessionId?: string;
  role?: 'referee' | 'viewer';
  sdp?: any;
  candidate?: any;
  message?: string;
  hasPeers?: boolean;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

function formatTime(): string {
  return new Date().toTimeString().slice(0, 8);
}

// ════════════════════════════════════════════════════════════════════════════════
// VIEWER WEB RTC MANAGER (Voor op de tablet / iPad)
// ════════════════════════════════════════════════════════════════════════════════
export class ViewerWebRtcManager {
  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private ws: WebSocket | null = null;
  private sessionId: string;
  private onStatusChange: (status: ConnectionStatus) => void;
  private onScoreUpdate: (payload: ScoreboardPayload) => void;
  private onLog?: (msg: string) => void;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private heartbeatInterval: number | null = null;
  private reconnectTimer: number | null = null;
  private wsUrl = '';
  private isDestroyed = false;

  constructor(
    sessionId: string,
    onStatusChange: (status: ConnectionStatus) => void,
    onScoreUpdate: (payload: ScoreboardPayload) => void,
    onLog?: (msg: string) => void
  ) {
    this.sessionId = sessionId;
    this.onStatusChange = onStatusChange;
    this.onScoreUpdate = onScoreUpdate;
    this.onLog = onLog;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleNetworkRecovery);
      document.addEventListener('visibilitychange', this.handleVisibilityRecovery);
    }
  }

  private log(msg: string) {
    const formatted = `[${formatTime()}] ${msg}`;
    console.log(`[Viewer WebRTC] ${formatted}`);
    if (this.onLog) {
      this.onLog(formatted);
    }
  }

  private handleNetworkRecovery = () => {
    this.log('Wi-Fi / Hotspot netwerk hersteld. Herverbinden...');
    this.reconnect();
  };

  private handleVisibilityRecovery = () => {
    if (document.visibilityState === 'visible') {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.log('Scherm actief geworden. Verbinding controleren...');
        this.reconnect();
      }
    }
  };

  public init(wsUrl: string) {
    this.wsUrl = wsUrl;
    this.isDestroyed = false;
    this.connectWs();
  }

  public setSessionId(newId: string) {
    if (this.sessionId === newId) return;
    this.sessionId = newId;
    this.log(`Nieuwe koppelcode ingesteld: ${newId}`);
    this.reconnect();
  }

  private reconnect() {
    if (this.isDestroyed) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch (_e) { /* ignore */ }
      this.ws = null;
    }
    this.connectWs();
  }

  private connectWs() {
    if (this.isDestroyed || !this.wsUrl) return;

    this.onStatusChange('connecting');
    this.log(`Verbinden met server (${this.wsUrl})...`);

    try {
      this.ws = new WebSocket(this.wsUrl);
    } catch (e: any) {
      this.log(`Fout bij aanmaken WebSocket: ${e.message}`);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.log(`WebSocket verbonden. Aanmelden bij sessie ${this.sessionId}...`);
      this.sendSignaling({
        type: 'join',
        sessionId: this.sessionId,
        role: 'viewer'
      });
    };

    this.ws.onmessage = async (event) => {
      try {
        const msg: SignalingMessage = JSON.parse(event.data);
        await this.handleSignaling(msg);
      } catch (err) {
        console.error('[Viewer WebRTC] Error parsing signaling message:', err);
      }
    };

    this.ws.onerror = (e) => {
      this.log('WebSocket netwerkfout (controleer hotspot verbinding)');
      if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
        this.onStatusChange('error');
      }
    };

    this.ws.onclose = () => {
      this.log('WebSocket verbinding gesloten');
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
        this.onStatusChange('disconnected');
      }
      this.scheduleReconnect();
    };

    // Heartbeat om Synology reverse proxy timeout te voorkomen
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);

    this.setupPeerConnection();
  }

  private scheduleReconnect() {
    if (this.isDestroyed || this.reconnectTimer) return;
    this.log('Automatisch herverbinden over 3s...');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isDestroyed) {
        this.connectWs();
      }
    }, 3000);
  }

  private setupPeerConnection() {
    try {
      const PeerConn = window.RTCPeerConnection || (window as any).webkitRTCPeerConnection;
      if (!PeerConn) {
        this.log('RTCPeerConnection niet beschikbaar, WebSocket relay blijft actief.');
        return;
      }

      if (this.pc) {
        try { this.pc.close(); } catch (_e) { /* ignore */ }
      }

      this.pc = new PeerConn(ICE_SERVERS);
      this.pendingCandidates = [];

      this.pc.onicecandidate = (event: any) => {
        if (event.candidate && this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.sendSignaling({
            type: 'candidate',
            sessionId: this.sessionId,
            candidate: event.candidate
          });
        }
      };

      this.pc.ondatachannel = (event: any) => {
        this.log('P2P DataChannel ontvangen van teller!');
        this.dataChannel = event.channel;
        this.bindDataChannel(this.dataChannel!);
      };

      this.pc.onconnectionstatechange = () => {
        const state = this.pc?.connectionState;
        if (state === 'connected') {
          this.log('Directe P2P link actief! (Ultra low latency)');
          this.onStatusChange('connected');
        } else if (state === 'failed' || state === 'disconnected') {
          this.log('P2P niet mogelijk via hotspot. WebSocket Relay blijft actief (100% realtime).');
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.onStatusChange('connected');
            this.sendSignaling({ type: 'request-score', sessionId: this.sessionId });
          }
        } else {
          this.log(`P2P status: ${state}`);
        }
      };
    } catch (e: any) {
      this.log(`WebRTC setup fout: ${e.message}`);
      this.pc = null;
    }
  }

  private bindDataChannel(channel: RTCDataChannel) {
    channel.onopen = () => {
      this.log('P2P DataChannel OPEN! Directe peer-to-peer actief.');
      this.onStatusChange('connected');
    };

    channel.onclose = () => {
      this.log('P2P DataChannel gesloten');
    };

    channel.onmessage = (event) => {
      try {
        const payload: ScoreboardPayload = JSON.parse(event.data);
        this.log(`Score ontvangen via P2P: ${payload.player1?.score ?? 0} - ${payload.player2?.score ?? 0}`);
        this.onScoreUpdate(payload);
        this.onStatusChange('connected');
      } catch (err) {
        console.error('[Viewer WebRTC] Failed to parse scoreboard payload from DataChannel:', err);
      }
    };
  }

  private async handleSignaling(msg: SignalingMessage | any) {
    if ((msg.type as string) === 'ping') return;

    if (msg.type === 'joined') {
      if (msg.hasPeers) {
        this.log(`Sessie ${this.sessionId} bevestigd: Teller is aanwezig! Verbonden.`);
        this.onStatusChange('connected');
        // Vraag direct de laatste score op
        this.sendSignaling({ type: 'request-score', sessionId: this.sessionId });
      } else {
        this.log(`Sessie ${this.sessionId} geregistreerd: Wachten op teller mobiel...`);
      }
      return;
    }

    if (msg.type === 'peer-joined') {
      this.log(`Teller heeft sessie ${this.sessionId} geopend! Verbonden.`);
      this.onStatusChange('connected');
      this.sendSignaling({ type: 'request-score', sessionId: this.sessionId });
      return;
    }

    if (msg.type === 'peer-left') {
      this.log('Teller heeft de verbinding verbroken');
      this.onStatusChange('connecting');
      return;
    }

    // Hybride Fallback: Realtime score-updates via WebSocket relay
    if (msg.type === 'SCORE_UPDATE' || msg.type === 'MATCH_RESET') {
      this.log(`Score ontvangen via Relay: ${msg.player1?.name || 'P1'} (${msg.player1?.score || 0}) - ${msg.player2?.name || 'P2'} (${msg.player2?.score || 0})`);
      this.onScoreUpdate(msg as ScoreboardPayload);
      this.onStatusChange('connected');
      return;
    }

    if (!this.pc) return;

    try {
      const SessionDesc = window.RTCSessionDescription || (window as any).webkitRTCSessionDescription;
      const IceCand = window.RTCIceCandidate || (window as any).webkitRTCIceCandidate;

      if (msg.type === 'offer' && msg.sdp) {
        this.log('P2P Offer ontvangen van teller. Answer genereren...');
        await this.pc.setRemoteDescription(new (SessionDesc || RTCSessionDescription)(msg.sdp));
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);

        this.sendSignaling({
          type: 'answer',
          sessionId: this.sessionId,
          sdp: answer
        });
        this.log('P2P Answer teruggestuurd naar teller');

        for (const c of this.pendingCandidates) {
          try { 
            await this.pc.addIceCandidate(new (IceCand || RTCIceCandidate)(c)); 
          } catch (_e) { /* ignore */ }
        }
        this.pendingCandidates = [];
      } else if (msg.type === 'candidate' && msg.candidate) {
        if (this.pc.remoteDescription) {
          try {
            await this.pc.addIceCandidate(new (IceCand || RTCIceCandidate)(msg.candidate));
          } catch (_e) { /* ignore */ }
        } else {
          this.pendingCandidates.push(msg.candidate);
        }
      }
    } catch (webrtcErr: any) {
      this.log(`WebRTC handshake waarschuwing: ${webrtcErr.message}`);
    }
  }

  private sendSignaling(data: SignalingMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  public destroy() {
    this.isDestroyed = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleNetworkRecovery);
      document.removeEventListener('visibilitychange', this.handleVisibilityRecovery);
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.onStatusChange('disconnected');
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// REFEREE WEB RTC MANAGER (Voor op de smartphone van de teller)
// ════════════════════════════════════════════════════════════════════════════════
export class RefereeWebRtcManager {
  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private ws: WebSocket | null = null;
  private sessionId: string;
  private onStatusChange: (status: ConnectionStatus) => void;
  private onLog?: (msg: string) => void;
  private onRequestScore?: () => void;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private heartbeatInterval: number | null = null;
  private reconnectTimer: number | null = null;
  private hasPeer = false;
  private wsUrl = '';
  private isDestroyed = false;

  constructor(
    sessionId: string,
    onStatusChange: (status: ConnectionStatus) => void,
    onLog?: (msg: string) => void,
    onRequestScore?: () => void
  ) {
    this.sessionId = sessionId;
    this.onStatusChange = onStatusChange;
    this.onLog = onLog;
    this.onRequestScore = onRequestScore;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleNetworkRecovery);
      document.addEventListener('visibilitychange', this.handleVisibilityRecovery);
    }
  }

  private log(msg: string) {
    const formatted = `[${formatTime()}] ${msg}`;
    console.log(`[Referee WebRTC] ${formatted}`);
    if (this.onLog) {
      this.onLog(formatted);
    }
  }

  private handleNetworkRecovery = () => {
    this.log('Netwerk hersteld. Herverbinden...');
    this.reconnect();
  };

  private handleVisibilityRecovery = () => {
    if (document.visibilityState === 'visible') {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.log('Scherm actief geworden. Verbinding controleren...');
        this.reconnect();
      }
    }
  };

  public init(wsUrl: string) {
    this.wsUrl = wsUrl;
    this.isDestroyed = false;
    this.connectWs();
  }

  private reconnect() {
    if (this.isDestroyed) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch (_e) { /* ignore */ }
      this.ws = null;
    }
    this.connectWs();
  }

  private connectWs() {
    if (this.isDestroyed || !this.wsUrl) return;

    this.onStatusChange('connecting');
    this.log(`Verbinden met server (${this.wsUrl})...`);

    try {
      this.ws = new WebSocket(this.wsUrl);
    } catch (e: any) {
      this.log(`Fout bij aanmaken WebSocket: ${e.message}`);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.log(`WebSocket verbonden. Koppelcode ${this.sessionId} registreren...`);
      this.sendSignaling({
        type: 'join',
        sessionId: this.sessionId,
        role: 'referee'
      });
    };

    this.ws.onmessage = async (event) => {
      try {
        const msg: SignalingMessage = JSON.parse(event.data);
        await this.handleSignaling(msg);
      } catch (err) {
        console.error('[Referee WebRTC] Error parsing signaling message:', err);
      }
    };

    this.ws.onerror = (e) => {
      this.log('WebSocket netwerkfout');
      if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
        this.onStatusChange('error');
      }
    };

    this.ws.onclose = () => {
      this.log('WebSocket gesloten');
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
      if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
        this.onStatusChange('disconnected');
      }
      this.scheduleReconnect();
    };

    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);
  }

  private scheduleReconnect() {
    if (this.isDestroyed || this.reconnectTimer) return;
    this.log('Herverbinden over 3s...');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isDestroyed) {
        this.connectWs();
      }
    }, 3000);
  }

  private async startHandshake() {
    if (this.pc) {
      try { this.pc.close(); } catch (_e) { /* ignore */ }
    }

    this.log('Starten van WebRTC P2P handshake met iPad...');
    this.pc = new RTCPeerConnection(ICE_SERVERS);
    this.pendingCandidates = [];

    this.pc.onicecandidate = (event) => {
      if (event.candidate && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.sendSignaling({
          type: 'candidate',
          sessionId: this.sessionId,
          candidate: event.candidate
        });
      }
    };

    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState;
      if (state === 'connected') {
        this.log('Directe P2P link actief! (Ultra low latency)');
        this.onStatusChange('connected');
      } else if (state === 'failed' || state === 'disconnected') {
        this.log('P2P geblokkeerd door hotspot. Schakelt over op WebSocket Relay (100% werkend).');
        if (this.ws && this.ws.readyState === WebSocket.OPEN && this.hasPeer) {
          this.onStatusChange('connected');
          if (this.onRequestScore) this.onRequestScore();
        }
      } else {
        this.log(`P2P status: ${state}`);
      }
    };

    this.dataChannel = this.pc.createDataChannel('squashScoreChannel', {
      ordered: true
    });

    this.dataChannel.onopen = () => {
      this.log('P2P DataChannel OPEN! Direct peer-to-peer actief.');
      this.onStatusChange('connected');
      if (this.onRequestScore) this.onRequestScore();
    };

    this.dataChannel.onclose = () => {
      this.log('P2P DataChannel gesloten');
    };

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    this.sendSignaling({
      type: 'offer',
      sessionId: this.sessionId,
      sdp: offer
    });
    this.log('Offer verzonden naar iPad');
  }

  private async handleSignaling(msg: SignalingMessage) {
    if ((msg.type as string) === 'ping') return;

    if (msg.type === 'peer-joined') {
      this.log(`iPad is zojuist kamer ${this.sessionId} binnengekomen! Verbonden.`);
      this.hasPeer = true;
      this.onStatusChange('connected');
      if (this.onRequestScore) this.onRequestScore();
      await this.startHandshake();
    } else if (msg.type === 'joined') {
      if (msg.hasPeers) {
        this.log(`Sessie ${this.sessionId} geopend: iPad is al aanwezig! Verbonden.`);
        this.hasPeer = true;
        this.onStatusChange('connected');
        if (this.onRequestScore) this.onRequestScore();
        await this.startHandshake();
      } else {
        this.log(`Sessie ${this.sessionId} geregistreerd: Wachten op iPad...`);
        this.hasPeer = false;
        this.onStatusChange('connecting');
      }
    } else if (msg.type === 'peer-left') {
      this.log('iPad heeft sessie verlaten. Wachten op herverbinding...');
      this.hasPeer = false;
      this.onStatusChange('connecting');
    } else if (msg.type === 'request-score') {
      this.log('iPad vraagt actuele stand op. Versturen...');
      if (this.onRequestScore) this.onRequestScore();
    } else if (msg.type === 'answer' && msg.sdp && this.pc) {
      this.log('Answer ontvangen van iPad. P2P link voltooien...');
      await this.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      for (const c of this.pendingCandidates) {
        try { await this.pc.addIceCandidate(new RTCIceCandidate(c)); } catch (_e) { /* ignore */ }
      }
      this.pendingCandidates = [];
    } else if (msg.type === 'candidate' && msg.candidate && this.pc) {
      if (this.pc.remoteDescription) {
        try {
          await this.pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
        } catch (_e) { /* ignore */ }
      } else {
        this.pendingCandidates.push(msg.candidate);
      }
    }
  }

  private sendSignaling(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  public sendScore(payload: ScoreboardPayload): boolean {
    let sent = false;

    // 1. Primair via direct P2P DataChannel indien beschikbaar
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      try {
        this.dataChannel.send(JSON.stringify(payload));
        sent = true;
      } catch (e) {
        console.warn('[Referee WebRTC] Error sending via DataChannel:', e);
      }
    }

    // 2. Altijd ook via WebSocket Relay voor 100% zekere bezorging (bijv. als hotspot P2P blokkeert)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(payload));
        sent = true;
      } catch (e) {
        console.warn('[Referee WebRTC] Error sending via WebSocket relay:', e);
      }
    }

    return sent;
  }

  public isConnected(): boolean {
    const dataChannelOpen = this.dataChannel?.readyState === 'open';
    const wsRelayReady = this.ws !== null && this.ws.readyState === WebSocket.OPEN && this.hasPeer;
    return !!(dataChannelOpen || wsRelayReady);
  }

  public destroy() {
    this.isDestroyed = true;
    this.hasPeer = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleNetworkRecovery);
      document.removeEventListener('visibilitychange', this.handleVisibilityRecovery);
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.onStatusChange('disconnected');
  }
}
